import { Plus, Trash2 } from 'lucide-react';
import type { ProductFormState } from './types';

interface StepVariantsProps {
  state: ProductFormState;
  updateState: (updater: ProductFormState | ((prev: ProductFormState) => ProductFormState)) => void;
  error?: string;
}

export function StepVariants({ state, updateState, error }: StepVariantsProps) {
  const addVariant = () => {
    updateState((prev) => ({
      ...prev,
      variants: [
        ...prev.variants,
        {
          id: Date.now().toString(),
          name: '',
          variantSku: '',
          variantFilaments: [],
          variantSupplies: [],
          filamentId: null,
          modelExternalUrl: '',
          designExternalUrl: '',
          designNotes: '',
          imageUrl: null,
          notes: '',
          initialQuantity: '0',
          locationId: '',
          binLocation: '',
          binNumber: '',
        },
      ],
    }));
  };

  const removeVariant = (id: string) => {
    if (state.variants.length <= 1) return;
    updateState((prev) => ({
      ...prev,
      variants: prev.variants.filter((v) => v.id !== id),
    }));
  };

  const updateVariant = (id: string, field: keyof ProductFormState['variants'][0], value: string) => {
    updateState((prev) => ({
      ...prev,
      variants: prev.variants.map((v) => (v.id === id ? { ...v, [field]: value } : v)),
    }));
  };

  const duplicateNames = (() => {
    const names = state.variants.map((v) => v.name.trim().toLowerCase()).filter(Boolean);
    return names.filter((name, idx) => names.indexOf(name) !== idx);
  })();

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-warm-cream">
          Variant Descriptions
        </h4>
        <p className="text-xs text-gray-600 dark:text-warm-sand/70 mt-1">
          Each color, finish, or style is a separate variant. Names must be unique.
        </p>
      </div>

      {(error || duplicateNames.length > 0) && (
        <div className="p-3 rounded-lg bg-danger-500/10 border border-danger-500/30 text-sm text-danger-400">
          {error ||
            `Duplicate variant names: ${[...new Set(duplicateNames)].join(', ')}`}
        </div>
      )}

      <div className="space-y-3">
        {state.variants.map((variant, index) => {
          const nameLower = variant.name.trim().toLowerCase();
          const isDuplicate =
            nameLower.length > 0 &&
            state.variants.filter((v) => v.name.trim().toLowerCase() === nameLower).length > 1;

          return (
            <div
              key={variant.id}
              className={`p-4 border rounded-xl space-y-3 ${
                isDuplicate
                  ? 'border-danger-500/50 bg-danger-500/5'
                  : 'border-gray-200 dark:border-charcoal-700 bg-white dark:bg-charcoal-800/30'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-warm-sand mb-1">
                      Description / Color {index === 0 && <span className="text-danger-400">*</span>}
                    </label>
                    <input
                      type="text"
                      value={variant.name}
                      onChange={(e) => updateVariant(variant.id, 'name', e.target.value)}
                      placeholder={
                        index === 0
                          ? 'Standard, Yellow/White, Black Matte...'
                          : 'Another color or finish...'
                      }
                      className={`w-full px-3 py-2 bg-white dark:bg-charcoal-800 border rounded-lg text-sm text-gray-900 dark:text-warm-cream focus:outline-none focus:border-amber-450 ${
                        isDuplicate ? 'border-danger-500' : 'border-gray-300 dark:border-charcoal-600'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-warm-sand mb-1">
                      Variant SKU <span className="text-gray-500">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={variant.variantSku}
                      onChange={(e) => updateVariant(variant.id, 'variantSku', e.target.value)}
                      placeholder="Auto-generated if blank"
                      className="w-full px-3 py-2 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-sm focus:outline-none focus:border-amber-450"
                    />
                  </div>
                </div>
                {state.variants.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeVariant(variant.id)}
                    className="p-2 text-danger-400 hover:bg-danger-500/10 rounded-lg flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-warm-sand mb-1">
                  Notes
                </label>
                <input
                  type="text"
                  value={variant.notes}
                  onChange={(e) => updateVariant(variant.id, 'notes', e.target.value)}
                  placeholder="Optional production notes..."
                  className="w-full px-3 py-2 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-sm focus:outline-none focus:border-amber-450"
                />
              </div>
            </div>
          );
        })}
      </div>

      <button type="button" onClick={addVariant} className="btn-secondary text-sm">
        <Plus className="w-4 h-4 mr-2 inline" />
        Add Another Description
      </button>
    </div>
  );
}
